import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import { playKey, playBeep, ensureAudioUnlocked } from '@/lib/audio';
import Admin from '@/Admin';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  RED: '#C41E2A',
  GREEN: '#22C55E',
  WHITE: '#FFFFFF',
  GREY: '#6D6E71',
  DISABLED: '#3a3a3a',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mobile-friendly short labels for module status lines
const MOBILE_LABELS = {
  'Design UI/UX': 'Design UI/UX',
  'Développement': 'Développement',
  'Contenu & Rédaction': 'Contenu & Réd.',
  'Tests & Optimisation': 'Tests & Optim.',
};

// ---- Module status line (shared by initial script + `progress` easter egg) ----
const buildModuleLine = (m, isMobile) => {
  const { RED, GREEN, GREY, DISABLED } = COLORS;
  const tag = m.status === 'DONE' ? '[DONE]' : m.status === 'WIP' ? '[>>>>]' : '[WAIT]';
  const lineColor = m.status === 'DONE' ? GREEN : m.status === 'WIP' ? RED : GREY;

  if (isMobile) {
    const label = MOBILE_LABELS[m.label] || m.label;
    const bars = 8;
    const fb = Math.round((m.progress / 100) * bars);
    const eb = bars - fb;
    const labelPadded = label.padEnd(15, ' ');
    const pct = String(m.progress).padStart(3) + '%';
    return {
      pulse: m.status === 'WIP',
      segments: [
        { text: '  ', color: lineColor },
        { text: tag, color: lineColor },
        { text: ' ' + labelPadded + ' ', color: lineColor },
        { text: '█'.repeat(fb), color: lineColor },
        { text: '░'.repeat(eb), color: DISABLED },
        { text: ' ' + pct, color: lineColor },
      ],
    };
  }

  const bars = 12;
  const fb = Math.round((m.progress / 100) * bars);
  const eb = bars - fb;
  const labelSegLen = 25;
  const dotsCount = Math.max(0, labelSegLen - m.label.length - 1);
  const dots = '.'.repeat(dotsCount);
  const pct = String(m.progress).padStart(3) + '%';
  return {
    pulse: m.status === 'WIP',
    segments: [
      { text: '  ', color: lineColor },
      { text: tag, color: lineColor },
      { text: ' ' + m.label + ' ' + dots + ' ', color: lineColor },
      { text: '█'.repeat(fb), color: lineColor },
      { text: '░'.repeat(eb), color: DISABLED },
      { text: ' ' + pct, color: lineColor },
    ],
  };
};

// ---- Script builder ----
const buildScript = (config, isMobile) => {
  const { RED, GREEN, WHITE, GREY, DISABLED } = COLORS;

  const gp = config.global_progress;
  const totalBlocks = 24;
  const filled = Math.round((gp / 100) * totalBlocks);
  const empty = totalBlocks - filled;

  return [
    { segments: [{ text: '> ENERGA SYSTEM v2.0', color: RED }] },
    { segments: [{ text: '> Refonte complète en cours...', color: WHITE }] },
    { segments: [{ text: '> Initialisation du nouveau site...', color: WHITE }], pauseAfter: 500 },
    { segments: [{ text: '[OK] Connexion serveur Yverdon-les-Bains', color: GREEN }], beep: true },
    { segments: [{ text: '[OK] Connexion serveur Lausanne', color: GREEN }], beep: true },
    { segments: [{ text: '[OK] Chargement modules CVSE-MCR', color: GREEN }], beep: true },
    { segments: [{ text: '[OK] Chargement Physique du bâtiment', color: GREEN }], beep: true },
    { segments: [{ text: '[OK] Chargement Rénovation énergétique', color: GREEN }], beep: true },
    { segments: [{ text: '[OK] Chargement Développement durable', color: GREEN }], beep: true, pauseAfter: 300 },
    { empty: true },
    { segments: [{ text: '> COMPILATION EN COURS...', color: RED }], blink: true },
    { empty: true },
    {
      segments: [
        { text: '[', color: WHITE },
        { text: '█'.repeat(filled), color: RED },
        { text: '░'.repeat(empty), color: DISABLED },
        { text: '] ', color: WHITE },
        { text: `${gp}% COMPLETE`, color: WHITE },
      ],
    },
    { empty: true },
    { segments: [{ text: '> Statut des modules :', color: GREY }] },
    ...config.modules.map((m) => buildModuleLine(m, isMobile)),
    { empty: true },
    { segments: [{ text: `> Lancement prévu : ${config.launch_date}`, color: WHITE }] },
    { empty: true },
    { segments: [{ text: '> Entrez votre email pour être notifié :', color: RED }] },
  ];
};

const lineLength = (line) => {
  if (line.empty) return 0;
  return line.segments.reduce((s, seg) => s + seg.text.length, 0);
};

const sliceLine = (line, limit) => {
  if (line.empty) return { empty: true };
  let consumed = 0;
  const out = [];
  for (const seg of line.segments) {
    if (consumed >= limit) break;
    const remaining = limit - consumed;
    const text = seg.text.slice(0, remaining);
    out.push({ ...seg, text });
    consumed += text.length;
  }
  return { segments: out, pulse: line.pulse, blink: line.blink };
};

// ---- LINE renderer ----
const LineView = ({ line }) => {
  if (line.empty) {
    return <div className="term-line" aria-hidden="true">{'\u00A0'}</div>;
  }
  const classes = ['term-line'];
  if (line.pulse) classes.push('pulse-line');
  if (line.blink) classes.push('blink-line');
  return (
    <div className={classes.join(' ')}>
      {line.segments.map((seg, i) => (
        seg.href ? (
          <a
            key={i}
            href={seg.href}
            className="term-link"
            style={{ color: seg.color }}
          >
            {seg.text}
          </a>
        ) : (
          <span key={i} style={{ color: seg.color }}>{seg.text}</span>
        )
      ))}
    </div>
  );
};

// ---- Main Terminal component ----
function Terminal() {
  const [config, setConfig] = useState(null);
  const [printed, setPrinted] = useState([]);
  const [current, setCurrent] = useState(null); // {line, typed}
  const [done, setDone] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const stored = localStorage.getItem('energa_sound');
      return stored === null ? true : stored === '1';
    } catch { return true; }
  });
  const [glitch, setGlitch] = useState(false);

  const scriptRef = useRef([]);
  const skipRef = useRef(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const bodyRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  const timeoutsRef = useRef([]);
  const runIdRef = useRef(0);
  const isMobileRef = useRef(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  // Reduced motion
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Fetch config on mount
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config`)
      .then((res) => { if (!cancelled) setConfig(res.data); })
      .catch(() => {
        if (!cancelled) {
          // Fallback to inline defaults — keeps the page usable if API hiccups.
          setConfig({
            global_progress: 67,
            launch_date: 'vendredi 13.11.2026',
            modules: [
              { label: 'Design UI/UX', status: 'DONE', progress: 100 },
              { label: 'Développement', status: 'WIP', progress: 67 },
              { label: 'Contenu & Rédaction', status: 'WIP', progress: 50 },
              { label: 'Tests & Optimisation', status: 'WAIT', progress: 0 },
            ],
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Build script + start typewriter when config arrives or after a reset
  const startSequence = useCallback((cfg) => {
    const script = buildScript(cfg, isMobileRef.current);
    scriptRef.current = script;
    setPrinted([]);
    setCurrent(null);
    setDone(false);
    skipRef.current = false;
    // cancel any pending timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    runIdRef.current += 1;
    const myRun = runIdRef.current;

    const charDelay = reducedMotion ? 0 : 50;

    const typeLine = (idx) => {
      if (myRun !== runIdRef.current) return;
      if (idx >= script.length) {
        setDone(true);
        setCurrent(null);
        return;
      }
      const line = script[idx];

      const advanceNext = () => {
        const pause = (line.pauseAfter && !skipRef.current && !reducedMotion) ? line.pauseAfter : 0;
        const t = setTimeout(() => typeLine(idx + 1), pause);
        timeoutsRef.current.push(t);
      };

      if (line.empty) {
        setPrinted((p) => [...p, line]);
        advanceNext();
        return;
      }

      const total = lineLength(line);

      if (skipRef.current || reducedMotion) {
        setPrinted((p) => [...p, line]);
        if (line.beep && soundOnRef.current && !skipRef.current) playBeep();
        // No pause when skipping
        const t = setTimeout(() => typeLine(idx + 1), 0);
        timeoutsRef.current.push(t);
        return;
      }

      let typed = 0;
      setCurrent({ line: sliceLine(line, 0), typed: 0, srcIndex: idx });

      const step = () => {
        if (myRun !== runIdRef.current) return;
        if (skipRef.current) {
          setCurrent(null);
          setPrinted((p) => [...p, line]);
          if (line.beep && soundOnRef.current) playBeep();
          const t = setTimeout(() => typeLine(idx + 1), 0);
          timeoutsRef.current.push(t);
          return;
        }
        typed += 1;
        const partial = sliceLine(line, typed);
        setCurrent({ line: partial, typed, srcIndex: idx });
        if (soundOnRef.current) playKey();
        if (typed >= total) {
          // Commit line, then advance
          setCurrent(null);
          setPrinted((p) => [...p, line]);
          if (line.beep && soundOnRef.current) playBeep();
          advanceNext();
        } else {
          const t = setTimeout(step, charDelay);
          timeoutsRef.current.push(t);
        }
      };

      const t0 = setTimeout(step, charDelay);
      timeoutsRef.current.push(t0);
    };

    typeLine(0);
  }, [reducedMotion]);

  useEffect(() => {
    if (config) startSequence(config);
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [config, startSequence]);

  // Skip-on-click/keypress intentionally REMOVED. The typewriter sequence
  // must always play to completion — accidental clicks during reading shall
  // not interrupt the animation.

  // Track viewport for mobile-friendly module status formatting.
  useEffect(() => {
    const onResize = () => {
      isMobileRef.current = window.innerWidth < 768;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-focus input when typewriter ends
  useEffect(() => {
    if (done && inputRef.current) {
      inputRef.current.focus();
    }
  }, [done]);

  // Auto-scroll terminal body to bottom whenever new content is rendered
  useEffect(() => {
    const el = bodyRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [printed, current, done]);

  // One-shot AudioContext unlock on first user gesture (browser autoplay policy).
  useEffect(() => {
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      ensureAudioUnlocked();
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('mousedown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
    window.addEventListener('keydown', unlock);
    window.addEventListener('mousedown', unlock);
    window.addEventListener('touchstart', unlock);
    return () => {
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('mousedown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Glitch effect every 15-20s
  useEffect(() => {
    if (reducedMotion) return undefined;
    let mounted = true;
    const schedule = () => {
      const delay = 15000 + Math.random() * 5000;
      const t = setTimeout(() => {
        if (!mounted) return;
        setGlitch(true);
        const t2 = setTimeout(() => {
          if (!mounted) return;
          setGlitch(false);
          schedule();
        }, 120);
        timeoutsRef.current.push(t2);
      }, delay);
      timeoutsRef.current.push(t);
    };
    schedule();
    return () => { mounted = false; };
  }, [reducedMotion]);

  // ---- Command handling ----
  const appendLine = (line) => setPrinted((p) => [...p, line]);

  const printModulesBlock = () => {
    if (!config) return;
    appendLine({ segments: [{ text: '> Statut des modules :', color: COLORS.GREY }] });
    config.modules.forEach((m) => {
      appendLine(buildModuleLine(m, isMobileRef.current));
    });
  };

  const submitEmail = async (email) => {
    appendLine({ segments: [{ text: `> ${email}`, color: COLORS.RED }] });
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      if (res.data && res.data.ok) {
        appendLine({
          segments: [
            { text: '> [SUCCESS] Email enregistré. Transmission confirmée. ✓', color: COLORS.GREEN },
          ],
        });
        appendLine({
          segments: [
            { text: '> En attendant tu peux nous écrire à ', color: COLORS.GREY },
            { text: 'info@energa.ch', color: COLORS.RED, href: 'mailto:info@energa.ch' },
            { text: ' ou nous appeler au ', color: COLORS.GREY },
            { text: '024 425 00 50', color: COLORS.RED, href: 'tel:+41244250050' },
          ],
        });
        if (soundOnRef.current) playBeep();
      } else {
        appendLine({
          segments: [
            { text: '> [ERREUR] Réponse inattendue du serveur.', color: COLORS.RED },
          ],
        });
      }
    } catch (err) {
      const status = err && err.response ? err.response.status : 0;
      const msg = status === 422
        ? '> [ERREUR] Format d\u2019email invalide.'
        : '> [ERREUR] Connexion serveur impossible. Réessayez plus tard.';
      appendLine({ segments: [{ text: msg, color: COLORS.RED }] });
    }
  };

  const handleSubmit = async () => {
    const raw = inputValue.trim();
    if (!raw) return;
    const cmd = raw.toLowerCase();

    // Easter eggs first
    if (cmd === 'help') {
      appendLine({ segments: [{ text: `> ${raw}`, color: COLORS.RED }] });
      appendLine({ segments: [{ text: '> Commandes : help | progress | contact | about | clear', color: COLORS.GREY }] });
      setInputValue('');
      return;
    }
    if (cmd === 'progress') {
      appendLine({ segments: [{ text: `> ${raw}`, color: COLORS.RED }] });
      printModulesBlock();
      setInputValue('');
      return;
    }
    if (cmd === 'contact') {
      appendLine({ segments: [{ text: `> ${raw}`, color: COLORS.RED }] });
      appendLine({ segments: [{ text: '> +41 24 425 00 50 | info@energa.ch', color: COLORS.WHITE }] });
      setInputValue('');
      return;
    }
    if (cmd === 'about') {
      appendLine({ segments: [{ text: `> ${raw}`, color: COLORS.RED }] });
      appendLine({ segments: [{ text: '> ENERGA SA | Ingénieurs-conseils EPFL-SIA | Yverdon & Lausanne', color: COLORS.WHITE }] });
      setInputValue('');
      return;
    }
    if (cmd === 'clear') {
      setInputValue('');
      if (config) startSequence(config);
      return;
    }

    // Email?
    if (EMAIL_RE.test(raw)) {
      setInputValue('');
      await submitEmail(raw);
      return;
    }

    appendLine({ segments: [{ text: `> ${raw}`, color: COLORS.RED }] });
    appendLine({ segments: [{ text: "> Commande inconnue. Tapez 'help' pour la liste.", color: COLORS.GREY }] });
    setInputValue('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem('energa_sound', next ? '1' : '0'); } catch { /* ignore */ }
    if (next) {
      ensureAudioUnlocked();
      playBeep();
    }
  };

  const focusInput = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="page-root">
      {/* Logo client officiel — fourni par ENERGA */}
      <div className="energa-logo-wrapper" data-testid="brand-logo">
        <img src="/logo.svg" alt="ENERGA" className="energa-logo" />
      </div>

      <main className="terminal-wrap">
        <div
          className={`terminal ${glitch ? 'is-glitching' : ''}`}
          ref={containerRef}
          onClick={focusInput}
          data-testid="terminal-container"
        >
          <div className="title-bar">
            <div className="title-dots">
              <span className="dot dot-red" />
              <span className="dot dot-yellow" />
              <span className="dot dot-green" />
            </div>
            <div className="title-text">ENERGA_SYSTEM_v2.0</div>
            <div className="title-spacer" />
          </div>

          <div className="terminal-body" ref={bodyRef}>
            {printed.map((line, i) => (
              <LineView key={`p-${i}`} line={line} />
            ))}
            {current && current.line && (
              <LineView line={current.line} />
            )}

            {done && (
              <div className="term-line input-line" data-testid="prompt-line">
                <span style={{ color: COLORS.RED }}>{'> '}</span>
                <span style={{ color: COLORS.RED }} className="input-display">
                  {inputValue}
                </span>
                <span className="cursor-block" aria-hidden="true">█</span>
                <input
                  ref={inputRef}
                  className="hidden-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={onKeyDown}
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  aria-label="Terminal command input"
                  data-testid="terminal-input"
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="page-footer" data-testid="page-footer">
        ENERGA SA • Ingénieurs-conseils EPFL-SIA • Yverdon-les-Bains | Lausanne
      </footer>

      <button
        type="button"
        className="sound-toggle"
        onClick={toggleSound}
        aria-label={soundOn ? 'Désactiver le son' : 'Activer le son'}
        data-testid="sound-toggle"
      >
        {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
      </button>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Terminal />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
